import { useState } from 'react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import { Spinner } from '@/components/ui/Spinner'
import { useContacts } from '@/hooks/useContacts'
import {
  Plus,
  Search,
  Users,
  Mail,
  Phone,
  Building2,
  MoreHorizontal,
} from 'lucide-react'

export default function ContactsPage() {
  const [search, setSearch] = useState('')
  const { contacts, isLoading } = useContacts({ search })

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-brown-900">
            Contacts
          </h1>
          <p className="mt-1 text-brown-600">
            Gerez vos contacts clients et prospects
          </p>
        </div>
        <Button leftIcon={<Plus className="w-4 h-4" />}>
          Nouveau contact
        </Button>
      </div>

      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="search"
              placeholder="Rechercher un contact..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<Search className="w-4 h-4" />}
            />
          </div>
        </div>
      </Card>

      {/* Contacts list */}
      <Card padding="none">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner />
          </div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-12 text-brown-500">
            <Users className="w-12 h-12 mx-auto mb-3 text-beige-300" />
            <p className="font-medium">Aucun contact trouve</p>
            <p className="text-sm mt-1">
              {search
                ? 'Essayez avec d\'autres termes de recherche'
                : 'Commencez par ajouter votre premier contact'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-beige-50 border-b border-beige-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider">
                    Telephone
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-beige-200">
                {contacts.map((contact) => (
                  <tr key={contact.id} className="hover:bg-beige-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-mint-100 flex items-center justify-center">
                          <span className="text-mint-700 font-semibold">
                            {contact.prenom.charAt(0)}
                            {contact.nom.charAt(0)}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-brown-900">
                            {contact.prenom} {contact.nom}
                          </div>
                          {contact.entreprise && (
                            <div className="text-sm text-brown-500 flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {contact.entreprise}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-brown-600">
                        <Mail className="w-4 h-4 mr-2 text-brown-400" />
                        {contact.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {contact.telephone ? (
                        <div className="flex items-center text-sm text-brown-600">
                          <Phone className="w-4 h-4 mr-2 text-brown-400" />
                          {contact.telephone}
                        </div>
                      ) : (
                        <span className="text-sm text-brown-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge
                        variant={contact.type === 'professionnel' ? 'gold' : 'mint'}
                      >
                        {contact.type === 'professionnel' ? 'Pro' : 'Particulier'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button className="text-brown-400 hover:text-brown-600">
                        <MoreHorizontal className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
